<script setup lang="ts">
import {
  boardingsGeneralText,
  castToEnquiryDtoAndSaveToStore,
  getAccosForGeneralEnquiry,
  getFilterDto,
  getPhonePlaceholder,
  titles
} from '@/composables/enquiry.helpers'
import { CID, CULTURE, PID, toFormattedStringDateShort } from '@/composables/helpers'
import type { IAccoItem, IListAccosApiResponse, ISaveEnquiryResponse } from '@/composables/interfaces/apiInterfaces'
import { type IBoarding, type IEnquiryFormValues, type IPackageDto, type IRoomDto } from '@/composables/interfaces/appInterfaces'
import { useListAccosApi, usePackageApi, useRoomApi, useSaveEnquiryApi } from '@/composables/useApi'
import { width } from '@/composables/windowSize'
import { mediaBreakpointsAsNumber, PetsNotAllowedAmenityId } from '@/constants'
import t from '@/i18n/translations'
import { date, pets, rooms } from '@/store/accosbar.store'
import {
  enquiryData,
  enquirySubmittedSuccessfully,
  landingPagePackageId,
  landingPageRoomId,
  setEnquiryDataFromLocalStorage,
  trackEnquiry
} from '@/store/enquiry.store'
import { filtersData } from '@/store/filter.store'
import { Form, FormField } from '@primevue/forms'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import emblaCarouselVue from 'embla-carousel-vue'
import Checkbox from 'primevue/checkbox'
import DatePicker from 'primevue/datepicker'
import IconField from 'primevue/iconfield'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Textarea from 'primevue/textarea'
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue'
import { z } from 'zod'
import AccoItemMini from '../accoitem/AccoItemMini.vue'
import AccoItemMiniCarousel from '../accoitem/AccoItemMiniCarousel.vue'
import AccoItemPlaceholder from '../accoitem/AccoItemPlaceholder.vue'
import AccosBarOccupancyPicker from '../occupancypicker/OccupancyPicker.vue'
import BoardingSvg from '../svgs/BoardingSvg.vue'
import DateSvg from '../svgs/DateSvg.vue'
import HouseSvg from '../svgs/HouseSvg.vue'
import PersonalDataSvg from '../svgs/PersonalDataSvg.vue'
import MobileSubmitButtonBottom from './EnquiryPageMobileSubmitButtonBottom.vue'
import PackageItem from './EnquiryPagePackageItem.vue'
import RoomItem from './EnquiryPageRoomItem.vue'
import EnquirySuccessPage from './EnquirySuccessPage.vue'

const formattedDateString = computed(() => toFormattedStringDateShort() ?? '')

const parsedHash = new URLSearchParams(window.location.hash.substring(1))
const cidFromFragment = parsedHash.get('cid')
const ridFromFragment = parsedHash.get('rid') //roomid
const pidFromFragment = parsedHash.get('pid') //packageid

const accoItem = ref<IAccoItem>() // used for direct enquiry
const boadingsForDirectEnquiry = ref<IBoarding[]>()
const directBoarding = ref<number | undefined>()
/**
 * used for general enquiry - never stored more then 80
 */
const accoItems = ref<IListAccosApiResponse>()
const roomItem = ref<IRoomDto>()
const packageItem = ref<IPackageDto>()

const directEnquiry = ref(false)
const cid = ref<number>()
const emptyEnquiry = ref(false)
const formSubmitting = ref(false)

const landingPageEnquiry = ref(false)

const enquiryError = ref<[boolean, string]>([false, ''])

const enquirySuccessSimilarAccos = ref<IAccoItem[] | null | undefined>()

const petsAllowed = ref<boolean>(true)

if (!!cidFromFragment) {
  directEnquiry.value = true
  cid.value = parseInt(cidFromFragment)
  const result = await useListAccosApi([cid.value])
  if (result.data != null) accoItem.value = result.data.accos[0]

  if (!!ridFromFragment) {
    const result = await useRoomApi(parseInt(ridFromFragment))
    if (result.data != null) roomItem.value = result.data
  }

  if (!!pidFromFragment) {
    const result = await usePackageApi(parseInt(pidFromFragment))
    if (result.data != null) packageItem.value = result.data
  }
}

//case: enquiry from landing page
if (CID) {
  directEnquiry.value = true
  landingPageEnquiry.value = true
  const result = await useListAccosApi([CID])
  if (result.data != null) accoItem.value = result.data.accos[0]
}

if (!!!cidFromFragment && !landingPageEnquiry.value && accoItems.value == null) emptyEnquiry.value = true

const maxLengthMessage = 1500
const formSchema = z.object({
  date: z
    .array(
      z.preprocess((val) => (val === '' ? undefined : val), z.date({ message: t(`${CULTURE}.enquiry.form.anagraph.errors.required.dateNotCorrect`) })),
      {
        message: t(`${CULTURE}.enquiry.form.anagraph.errors.required.dateToShort`)
      }
    )
    .length(2, { message: t(`${CULTURE}.enquiry.form.anagraph.errors.required.dateToShort`) }),
  title: z.string({ message: t(`${CULTURE}.enquiry.form.anagraph.errors.required.title`) }),
  firstname: z.string({ message: t(`${CULTURE}.enquiry.form.anagraph.errors.required.firstName`) }),
  surname: z.string({ message: t(`${CULTURE}.enquiry.form.anagraph.errors.required.lastName`) }),
  email: z
    .string({ message: t(`${CULTURE}.enquiry.form.anagraph.errors.required.email`) })
    .email({ message: t(`${CULTURE}.enquiry.form.anagraph.errors.valid.email`) }),
  phone: z.string().nullable().optional(),
  message: z
    .string()
    .max(maxLengthMessage, { message: t(`${CULTURE}.enquiry.form.anagraph.errors.required.message`).replace('{0}', maxLengthMessage) })
    .nullable()
    .optional(),
  privacy: z.literal<boolean>(true, { errorMap: () => ({ message: t(`${CULTURE}.enquiry.form.anagraph.errors.required.privacy`) }) })
})

const resolver = zodResolver(formSchema)

async function onFormSubmit(e: any) {
  if (!e.valid) {
    //this is needed to put the focus on the first invalid element. non focusable elements need the attribute tabindex="0" + element needs id with the z object property name
    const firstErrorValue: any = Object.values(e.errors)[0]
    const pathToFirstError = firstErrorValue[0].path[0]
    const invalidElementToFocus = document.getElementById(pathToFirstError)
    if (!!invalidElementToFocus) {
      invalidElementToFocus.focus()
    }
  } else if (e.valid && e.values != undefined) {
    const formValues: IEnquiryFormValues = e.values
    formValues.occupancy = { rooms: rooms.value, pets: pets.value }
    formSubmitting.value = true
    const result = await saveEnquiry(formValues)
    formSubmitting.value = false
    if (!result.success) {
      //unhappy path
      if (result.validationErrors != null) {
        const firstErrorKey = Object.keys(result.validationErrors)[0]
        const firstErrorMessage = result.validationErrors[firstErrorKey]
        enquiryError.value = [true, firstErrorMessage]
      } else {
        //if controller throws a exception
        enquiryError.value = [true, result.error!]
      }
    } else {
      //happy path
      enquirySuccessSimilarAccos.value = result.similarAccos
    }
  } else {
    //e.values is undefined, happens when a zod property is not set
    enquiryError.value = [true, t(`${CULTURE}.enquiry.form.anagraph.errors.general.general`)]
  }
}

async function saveEnquiry(formValues: IEnquiryFormValues): Promise<ISaveEnquiryResponse> {
  let cids: number[] | null = null
  if (landingPageEnquiry.value && !!CID) {
    //direct enquiry from landing page
    cids = [CID]
  } else if (!!cidFromFragment && accoItem.value != null) {
    //normal direct enquiry
    cids = [accoItem.value.companyId]
  } else {
    //general enquiry
    if (accoItems.value != null) cids = accoItems.value.accos.map((x: IAccoItem) => x.companyId)
  }
  if (cids == null) throw new Error('no companyid for enquiry')
  const filter = getFilterDto(cids, directBoarding.value)
  const roomTag = roomItem.value != null ? `Room|${roomItem.value?.roomId}` : undefined
  const packageTag = packageItem.value != null ? `Package|${packageItem.value?.packageId}` : undefined
  const enquiryData = castToEnquiryDtoAndSaveToStore(formValues, directEnquiry.value, cids, filter, roomTag, packageTag)
  var result = await useSaveEnquiryApi(enquiryData)
  if (result.success) {
    enquirySubmittedSuccessfully.value = result.success
    trackEnquiry(enquiryData, result, landingPageEnquiry.value)
  }
  return result
}

if (directEnquiry.value && accoItem.value != null && accoItem.value.availableBoardings != null) {
  boadingsForDirectEnquiry.value = filtersData.value?.boardings.filter((x) => accoItem.value!.availableBoardings!.includes(x.id))
  if (boadingsForDirectEnquiry.value != null) {
    directBoarding.value = boadingsForDirectEnquiry.value[0].id
  }
}

if (directEnquiry.value && accoItem.value != null && accoItem.value.amenityIds != null) {
  petsAllowed.value = !accoItem.value.amenityIds.includes(PetsNotAllowedAmenityId)
} else {
  petsAllowed.value = true
}

setEnquiryDataFromLocalStorage()

onBeforeMount(async () => {
  if (!directEnquiry.value) {
    const accos = await getAccosForGeneralEnquiry()
    if (accos != null) {
      accoItems.value = accos
      emptyEnquiry.value = false
    }
  }
})

const [emblaNode, emblaApi] = emblaCarouselVue({ loop: false })
let scrollNextDisabled = ref(false)
let scrollPrevDisabled = ref(false)

const scrollNext = () => emblaApi.value?.scrollNext()
const scrollPrev = () => emblaApi.value?.scrollPrev()
const initEmbla = (emblaApi: any) => {
  if (emblaApi == null) return
  scrollNextDisabled.value = !emblaApi.canScrollNext()
  scrollPrevDisabled.value = !emblaApi.canScrollPrev()
}

onMounted(() => {
  initEmbla(emblaApi.value)
  emblaApi.value?.on('select', initEmbla)
  emblaApi.value?.on('reInit', initEmbla)
})

watch(landingPageRoomId, async () => {
  if (!!landingPageRoomId.value) {
    const result = await useRoomApi(landingPageRoomId.value)
    if (result.data != null) roomItem.value = result.data
  }
})

watch(landingPagePackageId, async () => {
  if (!!landingPagePackageId.value) {
    const result = await usePackageApi(landingPagePackageId.value)
    if (result.data != null) packageItem.value = result.data
  }
})
</script>

<template>
  <div v-if="!enquirySubmittedSuccessfully" class="text-[var(--p-text-color)]">
    <h1 class="mb-6 font-medium md:text-3xl">
      {{ t(`${CULTURE}.enquiry.nonBinding`) }}
    </h1>

    <div v-if="directEnquiry" class="pui-card my-4 p-3 md:p-5">
      <div class="mb-4 flex items-center">
        <HouseSvg />
        <div class="ml-2 text-sm font-semibold md:text-xl">{{ t(`${CULTURE}.enquiry.directAcco`) }}</div>
      </div>
      <div class="flex flex-col gap-4 md:flex-row">
        <AccoItemMini v-if="accoItem != null" :item="accoItem" />
        <RoomItem v-if="roomItem" :item="roomItem!"></RoomItem>
        <PackageItem v-if="packageItem" :item="packageItem!"></PackageItem>
      </div>
    </div>

    <div v-else class="pui-card my-4 p-3 md:p-5">
      <div class="mb-4 flex items-center">
        <HouseSvg />
        <div class="ml-2 w-full text-sm font-semibold md:text-xl">
          {{ t(`${CULTURE}.enquiry.generalAcco[${accoItems?.accos.length != null && accoItems?.accos.length > 1 ? 'plural' : 'singular'}]`) }}
        </div>
      </div>
      <div class="embla acco-item-mini-carousel">
        <div class="embla__viewport relative overflow-hidden" ref="emblaNode">
          <div class="embla__container flex gap-2.5">
            <div v-if="!accoItems" class="h-24 w-full md:h-32">
              <AccoItemPlaceholder v-for="(_, index) in 4" :key="index" />
            </div>
            <AccoItemMiniCarousel v-else :item="accoItems.accos" />
          </div>
          <div class="embla__buttons absolute bottom-1/2 left-0 right-0 top-1/2 flex h-6 w-full -translate-y-1/2 transform">
            <button
              @click="scrollPrev"
              :disabled="scrollPrevDisabled"
              class="embla__button embla__button--prev bg-secondary absolute left-1 rounded-full border border-solid border-primary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#000" class="size-7 p-1 md:size-8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
              </svg>
            </button>
            <button
              @click="scrollNext"
              :disabled="scrollNextDisabled"
              class="embla__button embla__button--next bg-secondary absolute right-1 rounded-full border border-solid border-primary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#000" class="size-7 p-1 md:size-8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <Form id="enquiryform" :validateOnValueUpdate="false" :resolver="resolver" :validateOnBlur="false" :validateOnMount="false" @submit="onFormSubmit">
      <div class="pui-card rounded">
        <div class="grid p-3 md:grid-cols-2 md:p-5">
          <FormField v-slot="$field" name="date" :initialValue="date" class="flex flex-col">
            <div class="mb-4 flex items-center">
              <DateSvg />
              <div class="ml-2 text-sm font-semibold md:text-xl">{{ t(`${CULTURE}.enquiry.form.checkInCheckOut`) }} *</div>
            </div>
            <DatePicker
              id="date"
              tabindex="0"
              name="date"
              v-model="date"
              selectionMode="range"
              dateFormat="D, dd M."
              :minDate="new Date(new Date().setHours(0, 0, 0, 0))"
              :numberOfMonths="width <= 768 ? 1 : 2"
              :hideOnRangeSelection="true"
              :placeholder="formattedDateString"
              class="md:pl-6"
              :input-class="'cursor-pointer shadow-none'"
              pt:root:class="shadow-none"
              fluid
            >
            </DatePicker>
            <Message v-if="$field.invalid" severity="error" size="small" class="md:pl-6" variant="simple">{{ $field?.error.message }}</Message>
          </FormField>
          <div v-if="directEnquiry">
            <div class="mt-6 w-full md:mt-0">
              <div class="flex w-full flex-col md:px-6">
                <div class="mb-4 flex items-center">
                  <BoardingSvg />
                  <div class="ml-2 text-sm font-semibold md:text-xl">{{ t(`${CULTURE}.filter.boardings`) }}</div>
                </div>
                <div class="mt-6 w-full md:mt-0">
                  <div class="flex w-full flex-col md:px-6">
                    <select
                      v-model="directBoarding"
                      id="boarding"
                      class="cursor-pointer rounded-[var(--p-inputtext-border-radius)] border border-[var(--p-inputtext-border-color)] p-2"
                    >
                      <option v-for="board in boadingsForDirectEnquiry" :key="board.id.toString()" :value="board.id">
                        {{ board.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="mt-6 w-full md:mt-0">
              <div class="flex w-full flex-col md:px-6">
                <div class="mb-4 flex items-center">
                  <BoardingSvg />
                  <div class="ml-2 text-sm font-semibold md:text-xl">{{ t(`${CULTURE}.filter.boardings`) }}</div>
                </div>
                <div
                  id="boarding"
                  class="cursor-not-allowed rounded-[var(--p-inputtext-border-radius)] border border-[var(--p-inputtext-border-color)] bg-[#f2f2f2] p-2"
                >
                  {{ boardingsGeneralText() }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex justify-between p-4 md:justify-normal md:p-5 md:px-11 md:pt-0">
          <div class="my-1.5 flex items-center">
            <Checkbox v-model="pets.hasPets" inputId="checkbox" :binary="true" :disabled="!petsAllowed" :class="!petsAllowed ? 'cursor-not-allowed' : ''" />
            <label for="checkbox" class="ml-2 cursor-pointer">
              {{ petsAllowed === true ? t(`${CULTURE}.occupancy.animals`) : t(`${CULTURE}.occupancy.animalsNotAllowed`) }}
            </label>
          </div>
          <div class="ml-6 flex items-center gap-2" v-if="pets.hasPets">
            Info:
            <IconField>
              <InputText
                size="small"
                v-model="pets.message"
                class="w-40 rounded border-[var(--p-secondary-grey)] text-[var(--p-text-color)] shadow-none placeholder:text-gray-300"
                :placeholder="t(`${CULTURE}.occupancy.petsMessagePlaceholder`)"
              />
            </IconField>
          </div>
        </div>
      </div>

      <div class="flex flex-col">
        <AccosBarOccupancyPicker :roomItem="roomItem!" addRoomClass="md:mx-6" />
      </div>
      <div id="personaldata" class="pui-card mb-4 rounded p-3 md:p-5">
        <div class="mb-4 flex items-center">
          <PersonalDataSvg />
          <div class="ml-2 text-sm font-semibold md:text-xl">{{ t(`${CULTURE}.enquiry.form.personalData`) }}</div>
        </div>
        <div class="flex flex-col gap-4 md:grid md:grid-cols-3 md:px-6">
          <div class="flex flex-col">
            <FormField v-slot="$field" name="title" :initial-value="enquiryData?.title.valueOf()" class="flex flex-col">
              <span>{{ t(`${CULTURE}.enquiry.form.salutation`) }}*</span>
              <select
                id="title"
                name="title"
                tabindex="0"
                class="cursor-pointer rounded-[var(--p-inputtext-border-radius)] border border-[var(--p-inputtext-border-color)] p-2"
                :style="$field?.invalid ? 'border-color: var(--p-inputtext-invalid-border-color)' : ''"
                v-bind="$field.props"
                :value="$field.value"
              >
                <option v-for="title in titles()" :key="title.value" :value="title.value">
                  {{ title.name }}
                </option>
              </select>
              <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
          </div>

          <FormField v-slot="$field" name="firstname" :initial-value="enquiryData?.firstname" class="flex flex-col">
            <span>{{ t(`${CULTURE}.enquiry.form.firstname`) }}*</span>
            <InputText id="firstname" name="firstname" tabindex="0" type="text" class="shadow-none" fluid />
            <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
          </FormField>

          <FormField v-slot="$field" name="surname" :initial-value="enquiryData?.lastname" class="flex flex-col">
            <span>{{ t(`${CULTURE}.enquiry.form.surname`) }}*</span>
            <InputText id="surname" name="surname" tabindex="0" type="text" class="shadow-none" fluid />
            <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
          </FormField>

          <FormField v-slot="$field" name="email" :initial-value="enquiryData?.email" class="flex flex-col">
            <span>{{ t(`${CULTURE}.enquiry.form.email`) }}*</span>
            <InputText id="email" name="email" tabindex="0" type="text" class="shadow-none" fluid />
            <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
          </FormField>

          <FormField v-slot="$field" name="phone" :initial-value="enquiryData?.phone" class="flex flex-col">
            <span>{{ t(`${CULTURE}.enquiry.form.phone`) }} </span>
            <InputText id="phone" name="phone" :placeholder="getPhonePlaceholder()" class="shadow-none" fluid type="text" />
            <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
          </FormField>

          <div class="col-span-3 flex flex-col">
            <FormField v-slot="$field" name="message" class="flex flex-col">
              <span>{{ t(`${CULTURE}.enquiry.form.comments`) }}</span>
              <Textarea id="message" name="message" tabindex="0" class="shadow-none"></Textarea>
              <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
          </div>
        </div>
        <div class="md:px-6">
          <div class="pointer-events-none my-6 flex flex-row md:w-full">
            <FormField v-slot="$field" name="privacy" :initial-value="enquiryData?.privacy" class="pointer-events-auto flex flex-col">
              <label :for="'privacy'" class="flex cursor-pointer text-lg lg:text-base">
                <Checkbox inputId="privacy" name="privacy" :binary="true" />
                <span
                  class="ml-2 after:content-['*']"
                  v-html="
                    t(`${CULTURE}.enquiry.form.anagraph.labels.privacy`).replace(
                      '{privacyReplaceText}',
                      `<a class='underline underline-offset-4 decoration-primary decoration-2 font-semibold hover:bg-secondary' target='_blank' href='${filtersData!.privacyLink}'>${t(`${CULTURE}.enquiry.form.anagraph.labels.privacyReplaceText`)}</a>`
                    )
                  "
                ></span>
              </label>
              <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
          </div>
          <div class="pointer-events-none mb-6 flex flex-row md:w-full">
            <FormField v-slot="$field" name="newsletter" class="flex flex-col">
              <label :for="'newsletter'" class="pointer-events-auto flex cursor-pointer text-lg leading-4 md:w-3/5 lg:text-base">
                <Checkbox inputId="newsletter" name="newsletter" :binary="true" />
                <div class="ml-2 flex flex-col">
                  <span class="mb-2">
                    {{ t(`${CULTURE}.enquiry.form.anagraph.labels.newsletter`).replace('{portal}', t(`${CULTURE}.enquiry.form.anagraph.labels.portal${PID}`)) }}
                  </span>
                  <small class="leading-4">
                    {{
                      t(`${CULTURE}.enquiry.form.anagraph.subLabels.newsletter`).replace(
                        '{portal}',
                        t(`${CULTURE}.enquiry.form.anagraph.subLabels.portal${PID}`)
                      )
                    }}
                  </small>
                </div>
              </label>
              <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
          </div>
          <div v-if="enquiryError[0]" class="mb-2">
            <div class="rounded border border-red-500 bg-red-100 p-1 text-red-500">Error: {{ enquiryError[1] }}</div>
          </div>
          <div :class="!landingPageEnquiry ? 'hidden justify-end pb-4 md:flex' : 'flex justify-end pb-4'">
            <button
              v-if="!emptyEnquiry"
              :disabled="formSubmitting"
              type="submit"
              class="myEnquiryButton flex w-1/3 items-center justify-center rounded bg-primary py-1.5 text-center"
              @click="$window.siteEvent?.('button', 'enquiry-submit', '')"
            >
              {{ t(`${CULTURE}.enquirybar.enquiryButton`) }}
            </button>
            <button v-else type="submit" disabled class="bg-secondary whitespace-nowrap rounded border border-primary px-2 py-1 text-[var(--p-text-color)]">
              {{ t(`${CULTURE}.enquiry.form.anagraph.errors.required.emptyCard`) }}
            </button>
          </div>
        </div>
      </div>
      <MobileSubmitButtonBottom
        v-if="!landingPageEnquiry && width < mediaBreakpointsAsNumber.md"
        :empty-card="emptyEnquiry"
        :form-submitting="formSubmitting"
      ></MobileSubmitButtonBottom>
    </Form>
  </div>
  <div v-else>
    <EnquirySuccessPage :items="enquirySuccessSimilarAccos" :direct="directEnquiry" :lpenquiry="landingPageEnquiry" />
  </div>
</template>
